import { useEffect, useRef, useState, Fragment } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Truck, UploadSimple, CircleNotch, Plus, Trash, PencilSimple, CaretDown, CaretRight, CheckCircle, Eye } from "@phosphor-icons/react";
import { DocViewerModal, LockedBadge, isDocLocked } from "../../components/DocViewer";

const EMPTY = { type: "", brand: "", model: "", year: "", licensePlate: "", capacity: "" };
const DOC_STATUS = {
  PENDIENTE: { label: "Pendiente", cls: "bg-amber-100 text-amber-700" },
  APROBADO: { label: "Aprobado", cls: "bg-emerald-100 text-emerald-700" },
  RECHAZADO: { label: "Rechazado", cls: "bg-red-100 text-red-700" },
};

export default function CarrierVehicles() {
  const [vehicles, setVehicles] = useState(null);
  const [docs, setDocs] = useState([]);
  const [config, setConfig] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [viewFile, setViewFile] = useState(null);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);

  const load = () => api.get("/carrier/profile").then((r) => { setVehicles(r.data.vehicles || []); setDocs(r.data.documents || []); }).catch(() => setVehicles([]));
  const loadBrands = () => api.get("/carrier/vehicle-brands").then((r) => setBrands(r.data)).catch(() => setBrands([]));
  useEffect(() => { load(); loadBrands(); api.get("/meta/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!editing) return;
    const b = (form.brand || "").trim();
    api.get("/carrier/vehicle-models", { params: b ? { brand: b } : {} }).then((r) => setModels(r.data)).catch(() => setModels([]));
  }, [form.brand, editing]);

  const fmtKg = (raw) => {
    const digits = String(raw ?? "").replace(/\D/g, "");
    return digits ? Number(digits).toLocaleString("es-CL") : "";
  };

  const fmtPlate = (raw) => {
    const s = String(raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
    if (/^[A-Z]{4}\d{1,2}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4)}`;
    if (/^[A-Z]{2}\d{1,4}$/.test(s)) return `${s.slice(0, 2)}-${s.slice(2)}`;
    return s;
  };

  const currentYear = new Date().getFullYear();
  const yearValid = (y) => /^\d{4}$/.test(String(y || "")) && Number(y) >= currentYear - 30 && Number(y) <= currentYear + 1;

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (v) => { setForm({ ...EMPTY, ...v, capacity: fmtKg(v.capacity), licensePlate: fmtPlate(v.licensePlate) }); setEditing(v.id); };
  const close = () => { setEditing(null); setForm(EMPTY); };
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const save = async () => {
    if (!form.type) return toast.error("Selecciona el tipo de vehículo");
    if (!yearValid(form.year)) return toast.error(`El año debe tener 4 dígitos y el vehículo no puede superar los 30 años (${currentYear - 30}–${currentYear + 1})`);
    const plate = (form.licensePlate || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    if (plate && !/^([A-Z]{4}\d{2}|[A-Z]{2}\d{4})$/.test(plate)) return toast.error("Patente inválida. Formato chileno: BBBB12 o AB1234");
    setBusy(true);
    try {
      const body = { type: form.type, brand: form.brand, model: form.model, year: form.year, licensePlate: plate, capacity: form.capacity ? `${form.capacity} kg` : "" };
      if (editing === "new") await api.post("/carrier/vehicles", body);
      else await api.put(`/carrier/vehicles/${editing}`, body);
      toast.success("Vehículo guardado");
      close(); load(); loadBrands();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este vehículo?")) return;
    try { await api.delete(`/carrier/vehicles/${id}`); toast.success("Vehículo eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const uploadDoc = async (vehicleId, type, expiry, file) => {
    if (!file) return;
    setUploading(`${vehicleId}:${type}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/carrier/documents", { documentType: type, fileId: up.data.id, expirationDate: expiry || "", vehicleId });
      toast.success("Documento cargado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
    setUploading(null);
  };

  if (!vehicles) return <div className="flex justify-center py-24"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>;

  const reqDocs = config.vehicleDocs || [];
  const requiredSet = new Set((config.requiredDocs || []).filter((t) => reqDocs.includes(t)));
  const docSummary = (id) => {
    const own = docs.filter((d) => d.vehicleId === id);
    const approved = [...requiredSet].filter((t) => own.some((d) => d.documentType === t && d.status === "APROBADO")).length;
    return { approved, total: requiredSet.size };
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8" data-testid="carrier-vehicles">
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Acreditación</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Vehículos</h1>
          <p className="text-sm text-zinc-500 mt-1">Registra tu flota y los documentos obligatorios de cada vehículo.</p>
        </div>
        <button onClick={openNew} data-testid="add-vehicle-btn"
          className="px-5 py-2.5 rounded-full bg-[#FF5A36] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95 shrink-0">
          <Plus size={18} weight="bold" /> Agregar vehículo
        </button>
      </div>

      {vehicles.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-10 text-center text-zinc-500" data-testid="vehicles-empty">
          Aún no registras vehículos. Agrega al menos uno para poder ofertar servicios.
        </div>
      ) : (
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="vehicles-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-zinc-400 bg-zinc-50 border-b border-zinc-200">
                <th className="px-4 py-3 font-bold w-8"></th>
                <th className="px-4 py-3 font-bold">Vehículo</th>
                <th className="px-4 py-3 font-bold">Patente</th>
                <th className="px-4 py-3 font-bold">Capacidad</th>
                <th className="px-4 py-3 font-bold">Año</th>
                <th className="px-4 py-3 font-bold">Documentos</th>
                <th className="px-4 py-3 font-bold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map((v) => {
                const sum = docSummary(v.id);
                const complete = sum.total > 0 && sum.approved === sum.total;
                const open = !!expanded[v.id];
                return (
                  <Fragment key={v.id}>
                    <tr className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`vehicle-row-${v.id}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => toggle(v.id)} data-testid={`vehicle-expand-${v.id}`} className="text-zinc-500 hover:text-[#FF5A36]">
                          {open ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg border border-zinc-200 bg-zinc-50 flex items-center justify-center shrink-0"><Truck size={20} className="text-zinc-400" /></div>
                          <span className="font-semibold whitespace-nowrap">{v.type}{v.brand ? ` · ${v.brand}` : ""} {v.model}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-600">{v.licensePlate || "—"}</td>
                      <td className="px-4 py-3 text-zinc-600">{v.capacity || "—"}</td>
                      <td className="px-4 py-3 text-zinc-600">{v.year || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${complete ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {complete && <CheckCircle size={12} weight="fill" />}{sum.approved}/{sum.total} aprobados
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openEdit(v)} data-testid={`edit-vehicle-${v.id}`} className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-600"><PencilSimple size={16} /></button>
                          <button onClick={() => del(v.id)} data-testid={`del-vehicle-${v.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash size={16} /></button>
                        </div>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-zinc-50/60" data-testid={`vehicle-docs-${v.id}`}>
                        <td colSpan={7} className="px-4 py-4">
                          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Documentos del vehículo</div>
                          <table className="w-full text-sm bg-white rounded-xl border border-zinc-200 overflow-hidden">
                            <thead>
                              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-400 border-b border-zinc-100">
                                <th className="px-3 py-2 font-bold">Documento</th>
                                <th className="px-3 py-2 font-bold">Vencimiento</th>
                                <th className="px-3 py-2 font-bold">Estado</th>
                                <th className="px-3 py-2 font-bold text-right">Acción</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reqDocs.map((type) => (
                                <VehicleDocRow key={type} vehicleId={v.id} type={type}
                                  required={(config.requiredDocs || []).includes(type)}
                                  needsExpiry={(config.docsWithExpiry || []).includes(type)}
                                  docs={docs.filter((x) => x.vehicleId === v.id)}
                                  uploading={uploading} onUpload={uploadDoc}
                                  onView={(fid) => setViewFile({ id: fid, title: type })} />
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="vehicle-form-modal">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2"><Truck size={22} className="text-[#FF5A36]" /> {editing === "new" ? "Nuevo vehículo" : "Editar vehículo"}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Tipo de vehículo</label>
                <select data-testid="vehicle-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="mt-1.5 w-full h-11 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]">
                  <option value="">Selecciona</option>
                  {(config.vehicleTypes || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <VField label="Marca" testid="vehicle-brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v, model: "" })} list="vehicle-brand-list" placeholder="Escribe o elige" />
              <VField label="Modelo" testid="vehicle-model" value={form.model} onChange={(v) => setForm({ ...form, model: v })} list="vehicle-model-list" placeholder={form.brand ? "Escribe o elige" : "Primero la marca"} />
              <datalist id="vehicle-brand-list">{brands.map((b) => <option key={b} value={b} />)}</datalist>
              <datalist id="vehicle-model-list">{models.map((m) => <option key={m} value={m} />)}</datalist>
              <p className="sm:col-span-2 -mt-1 text-xs text-zinc-500" data-testid="brand-model-hint">Escribe la marca y el modelo. Si no aparecen en la lista, registraremos lo que escribas.</p>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Año</label>
                <div className="mt-1.5 relative">
                  <input data-testid="vehicle-year" value={form.year} inputMode="numeric" placeholder="2025"
                    onChange={(e) => setForm({ ...form, year: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                    className={`w-full h-11 pl-4 pr-10 rounded-xl border focus:outline-none focus:ring-2 transition-colors ${yearValid(form.year) ? "border-emerald-500 bg-emerald-50 focus:ring-emerald-500" : (form.year ? "border-red-400 focus:ring-red-400" : "border-zinc-300 focus:ring-[#FF5A36]")}`} />
                  {yearValid(form.year) && <CheckCircle size={18} weight="fill" className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500" data-testid="year-valid" />}
                </div>
                {form.year && !yearValid(form.year) && <p className="text-xs text-red-600 mt-1" data-testid="year-error">4 dígitos y máx. 30 años de antigüedad</p>}
              </div>
              <VField label="Patente" testid="vehicle-plate" value={form.licensePlate} onChange={(v) => setForm({ ...form, licensePlate: fmtPlate(v) })} placeholder="BBBB12 · AB1234" />
              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Capacidad (kg)</label>
                <div className="mt-1.5 relative">
                  <input data-testid="vehicle-capacity" value={form.capacity} inputMode="numeric"
                    onChange={(e) => setForm({ ...form, capacity: fmtKg(e.target.value) })} placeholder="1.000"
                    className="w-full h-11 pl-4 pr-12 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-zinc-400">kg</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={close} className="flex-1 py-3 rounded-xl border border-zinc-300 font-semibold hover:bg-zinc-50">Cancelar</button>
              <button onClick={save} disabled={busy} data-testid="vehicle-save"
                className="flex-1 py-3 rounded-xl bg-[#FF5A36] text-white font-bold hover:bg-[#E04B2A] disabled:opacity-60 active:scale-95">
                {busy ? "Guardando…" : "Guardar vehículo"}
              </button>
            </div>
          </div>
        </div>
      )}

      <DocViewerModal fileId={viewFile?.id} title={viewFile?.title} onClose={() => setViewFile(null)} />
    </div>
  );
}

function VField({ label, value, onChange, placeholder, testid, list }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">{label}</label>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} list={list}
        className="mt-1.5 w-full h-11 px-4 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
    </div>
  );
}

function VehicleDocRow({ vehicleId, type, required, needsExpiry, docs, uploading, onUpload, onView }) {
  const [expiry, setExpiry] = useState("");
  const fileRef = useRef(null);
  const doc = docs.filter((d) => d.documentType === type).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))[0];
  const st = doc ? (DOC_STATUS[doc.status] || DOC_STATUS.PENDIENTE) : null;
  const busy = uploading === `${vehicleId}:${type}`;
  const locked = isDocLocked(doc);
  return (
    <tr className="border-b border-zinc-50 last:border-0" data-testid={`vehicle-doc-${vehicleId}-${type}`}>
      <td className="px-3 py-2">
        <span className="font-medium">{type}</span>
        {required && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-[#E04B2A]">Obligatorio</span>}
        {doc?.status === "RECHAZADO" && doc.rejectionReason && <div className="text-xs text-red-600 mt-0.5">Motivo: {doc.rejectionReason}</div>}
      </td>
      <td className="px-3 py-2">
        {needsExpiry ? (
          <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid={`vehicle-doc-expiry-${vehicleId}-${type}`} disabled={locked}
            className="h-9 px-2 rounded-lg border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5A36] disabled:bg-zinc-50" />
        ) : <span className="text-zinc-400">—</span>}
      </td>
      <td className="px-3 py-2">
        {st ? <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
          : <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">Falta</span>}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="inline-flex items-center gap-2 justify-end">
          {doc?.fileId && (
            <button onClick={() => onView(doc.fileId)} data-testid={`vehicle-doc-view-${vehicleId}-${type}`}
              className="h-9 px-3 rounded-lg border border-zinc-300 text-sm font-semibold inline-flex items-center gap-1.5 hover:border-[#FF5A36] transition-colors">
              <Eye size={16} /> Ver
            </button>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden"
            onChange={(e) => { const f = e.target.files[0]; if (needsExpiry && !expiry) { toast.error("Indica la fecha de vencimiento primero"); e.target.value = ""; return; } onUpload(vehicleId, type, expiry, f); e.target.value = ""; }} />
          {locked ? <LockedBadge /> : (
            <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`vehicle-doc-upload-${vehicleId}-${type}`}
              className="h-9 px-4 rounded-lg border border-dashed border-zinc-400 text-sm font-semibold inline-flex items-center gap-1.5 hover:border-[#FF5A36] transition-colors disabled:opacity-60">
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />} {doc ? "Reemplazar" : "Subir"}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
